<template>
  <div>
    <s-crud
      title="Documentos por Puesto"
      :config="config"
      :filter="filter"
      add
      edit
      remove
      @save="save($event)"
      @rowSelected="rowSelected($event)"
      @clearForm="clearForm"
      ref="scrud"
    >
      <template v-slot:filter>
        <v-container>
          <v-row justify="center">
            <v-col cols="12" md="4" lg="3">
              <s-select-position
                label="Filtrar por Puesto"
                v-model="filter.PstID"
                :text="filterPstName"
                clearable
              />
            </v-col>
          </v-row>
        </v-container>
      </template>

      <!-- Slot para columna PDF Entregable -->
      <template v-slot:PdcFileName="{ row }">
        <div v-if="row.PdcFileName" class="d-flex align-center">
          <v-chip
            x-small
            color="red lighten-5"
            text-color="red darken-3"
            class="font-weight-medium text-truncate"
            style="max-width: 170px; cursor: pointer;"
            @click.stop="downloadCurrentFile(row.PdcFileName)"
            :title="'Descargar ' + row.PdcFileName"
          >
            <v-icon x-small left color="red darken-2">mdi-file-pdf-box</v-icon>
            {{ row.PdcFileName }}
          </v-chip>
        </div>
        <span v-else class="caption grey--text text--lighten-1 font-italic">-</span>
      </template>

      <!-- Slot para columna General -->
      <template v-slot:IsGeneral="{ row }">
        <v-chip
          x-small
          :color="row.IsGeneral ? 'teal lighten-5' : 'blue-grey lighten-5'"
          :text-color="row.IsGeneral ? 'teal darken-4' : 'blue-grey darken-3'"
          class="font-weight-bold"
        >
          <v-icon x-small left :color="row.IsGeneral ? 'teal' : 'blue-grey'">
            {{ row.IsGeneral ? 'mdi-earth' : 'mdi-briefcase-outline' }}
          </v-icon>
          {{ row.IsGeneral ? 'General' : 'Por Puestos' }}
        </v-chip>
      </template>

      <!-- Slot para columna Puestos Asignados -->
      <template v-slot:PstNames="{ row }">
        <div v-if="row.IsGeneral" class="d-flex align-center">
          <v-chip x-small color="teal lighten-5" text-color="teal darken-4" class="font-weight-medium">
            <v-icon x-small left color="teal">mdi-check-all</v-icon>
            Aplica a todos los puestos
          </v-chip>
        </div>
        <div v-else-if="!row.PstNames || !row.PstNames.trim()" class="d-flex align-center">
          <span class="caption grey--text text--darken-1 font-italic">Sin puestos asignados</span>
        </div>
        <div v-else class="d-flex align-center flex-wrap" style="max-width: 500px;">
          <!-- Dropdown / Popover con conteo y lista completa -->
          <v-menu
            offset-y
            open-on-hover
            close-on-content-click
            max-width="380"
            nudge-top="5"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-chip
                small
                color="primary lighten-5"
                text-color="primary"
                class="font-weight-bold mr-1 my-1"
                v-bind="attrs"
                v-on="on"
                style="cursor: pointer;"
              >
                <v-icon x-small left color="primary">mdi-briefcase-check</v-icon>
                {{ parsePositionList(row.PstNames).length }} puesto(s)
                <v-icon x-small right color="primary">mdi-chevron-down</v-icon>
              </v-chip>
            </template>
            <v-card class="pa-2 rounded-lg elevation-4" style="max-height: 280px; overflow-y: auto;">
              <div class="caption font-weight-bold primary--text mb-2 px-1 border-bottom pb-1">
                Puestos asignados ({{ parsePositionList(row.PstNames).length }})
              </div>
              <v-chip
                v-for="(pst, idx) in parsePositionList(row.PstNames)"
                :key="idx"
                x-small
                color="grey lighten-3"
                class="mr-1 mb-1 text-caption"
              >
                {{ pst }}
              </v-chip>
            </v-card>
          </v-menu>

          <!-- Previsualización de hasta 2 primeros puestos como chips compactos -->
          <v-chip
            v-for="(pst, idx) in parsePositionList(row.PstNames).slice(0, 2)"
            :key="idx"
            x-small
            outlined
            color="grey darken-2"
            class="mr-1 my-1 text-truncate"
            style="max-width: 140px;"
            :title="pst"
          >
            {{ pst }}
          </v-chip>
          <span
            v-if="parsePositionList(row.PstNames).length > 2"
            class="caption grey--text text--darken-1 font-weight-medium ml-1"
          >
            +{{ parsePositionList(row.PstNames).length - 2 }} más
          </span>
        </div>
      </template>

      <template v-slot="props">
        <v-container>
          <!-- Título del Documento -->
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="props.item.PdcDocumentTitle"
                label="Título del Documento *"
                outlined
                dense
                :rules="[rules.required]"
                placeholder="Ej: Reglamento RISST, Código de Conducta, etc."
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-combobox
                v-model="props.item.PdcComponentName"
                :items="availableComponents"
                label="Componente Vue (Formato a Firmar)"
                outlined
                dense
                :rules="[rules.requiredComponentOrFile]"
                clearable
                placeholder="Ej: FormatRisst, FormatRit, etc."
              >
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>{{ item }}</v-list-item-title>
                  </v-list-item-content>
                </template>
              </v-combobox>
            </v-col>
          </v-row>

          <!-- Carga de archivos PDF (Entregable) - Modo Nuevo -->
          <v-row v-if="!props.item.PdcID">
            <v-col cols="12">
              <s-load-file
                label="Seleccionar archivo(s) PDF Entregable (opcional)"
                :accept="'.pdf'"
                multiple
                @changeData="onFilesChanged"
              />
            </v-col>
          </v-row>

          <!-- Carga/Edición de archivo PDF (Entregable) - Modo Edición -->
          <v-row v-else>
            <v-col cols="12">
              <div
                v-if="props.item.PdcFileName"
                class="mb-2 d-flex align-center flex-wrap"
              >
                <span
                  class="text-caption grey--text text--darken-2 mr-2 font-weight-bold"
                  >Archivo actual:</span
                >
                <v-chip
                  color="primary lighten-5"
                  text-color="primary"
                  small
                  class="mr-2"
                  style="cursor: pointer"
                  @click="downloadCurrentFile(props.item.PdcFileName)"
                >
                  <v-icon x-small left color="primary">mdi-file-pdf-box</v-icon>
                  {{ props.item.PdcFileName }}
                  <v-icon x-small right color="primary">mdi-download</v-icon>
                </v-chip>
              </div>
              <div v-else class="mb-2">
                <span class="text-caption grey--text text--darken-1 font-italic"
                  >Sin archivo PDF entregable asignado</span
                >
              </div>
              <s-load-file
                label="Reemplazar / Asignar archivo PDF Entregable (opcional)"
                :accept="'.pdf'"
                :multiple="false"
                @changeData="onFilesChanged"
              />
            </v-col>
          </v-row>

          <!-- Documento General vs Asignación por Puestos -->
          <v-row>
            <v-col cols="12">
              <v-card outlined class="pa-3 mb-2 grey lighten-5">
                <v-row align="center">
                  <v-col cols="12" md="4">
                    <v-switch
                      v-model="props.item.IsGeneral"
                      label="Documento General (aplica a todos los puestos)"
                      color="primary"
                      hide-details
                      class="mt-0"
                    />
                  </v-col>
                  <v-col
                    cols="12"
                    md="8"
                    class="d-flex align-center justify-end flex-wrap"
                  >
                    <v-chip
                      small
                      color="deep-purple lighten-5"
                      text-color="deep-purple darken-3"
                      class="mr-2 mb-1"
                    >
                      <v-icon left x-small color="deep-purple"
                        >mdi-briefcase-check</v-icon
                      >
                      Total Seleccionados:
                      <strong>{{ selectedPstIDs.length }}</strong>
                    </v-chip>
                    <v-chip
                      small
                      color="blue lighten-5"
                      text-color="blue darken-3"
                      class="mr-2 mb-1"
                    >
                      <v-icon left x-small color="blue"
                        >mdi-filter-variant</v-icon
                      >
                      Coinciden con Filtros:
                      <strong>{{ filteredPositions.length }}</strong>
                    </v-chip>
                    <v-btn
                      v-if="!props.item.IsGeneral"
                      x-small
                      color="teal darken-1"
                      dark
                      class="mb-1 text-none"
                      @click="dialogManagePositions = true"
                    >
                      <v-icon x-small left>mdi-table-cog</v-icon>
                      Gestionar en Tabla
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
          </v-row>

          <!-- PANEL DE FILTROS DE PUESTOS (cuando NO es documento general) -->
          <v-row v-if="!props.item.IsGeneral">
            <v-col cols="12">
              <v-expansion-panels
                v-model="filterPanelOpen"
                class="elevation-1 mb-3"
              >
                <v-expansion-panel>
                  <v-expansion-panel-header class="py-2 px-3 bg-filter-header">
                    <div class="d-flex align-center flex-wrap">
                      <v-icon color="primary" class="mr-2" small
                        >mdi-filter-cog</v-icon
                      >
                      <span
                        class="font-weight-bold text-subtitle-2 primary--text mr-2"
                      >
                        Filtros de Puesto (Planillas, Jerarquías, Categorías,
                        etc.)
                      </span>
                      <v-chip
                        x-small
                        :color="
                          activeFilterCount > 0 ? 'primary' : 'grey lighten-2'
                        "
                        :text-color="
                          activeFilterCount > 0 ? 'white' : 'grey darken-2'
                        "
                      >
                        {{ activeFilterCount }} filtro(s) activo(s)
                      </v-chip>
                    </div>
                  </v-expansion-panel-header>

                  <v-expansion-panel-content class="pt-3">
                    <!-- Fila 1: Filtros Multiselect Principales -->
                    <v-row dense>
                      <!-- Planillas (Multiselect) -->
                      <v-col cols="12" md="4">
                        <v-autocomplete
                          v-model="filterPayrolls"
                          :items="payrollOptions"
                          item-text="DedDescription"
                          item-value="DedHelperValue"
                          label="Planilla (Multiselect)"
                          multiple
                          chips
                          small-chips
                          deletable-chips
                          clearable
                          outlined
                          dense
                          hide-details
                          placeholder="Todas las planillas"
                        >
                          <template v-slot:selection="{ item, index }">
                            <v-chip
                              v-if="index < 2"
                              x-small
                              color="purple lighten-4"
                              text-color="purple darken-4"
                            >
                              {{ item.DedDescription }}
                            </v-chip>
                            <span
                              v-else-if="index === 2"
                              class="grey--text text-caption"
                            >
                              +{{ filterPayrolls.length - 2 }}
                            </span>
                          </template>
                        </v-autocomplete>
                      </v-col>

                      <!-- Nivel Jerárquico (Multiselect) -->
                      <v-col cols="12" md="4">
                        <v-autocomplete
                          v-model="filterHierarchicalLevels"
                          :items="hierarchicalLevelOptions"
                          item-text="HlvName"
                          item-value="HlvID"
                          label="Nivel Jerárquico (Multiselect)"
                          multiple
                          chips
                          small-chips
                          deletable-chips
                          clearable
                          outlined
                          dense
                          hide-details
                          placeholder="Todos los niveles"
                        >
                          <template v-slot:selection="{ item, index }">
                            <v-chip
                              v-if="index < 2"
                              x-small
                              color="indigo lighten-4"
                              text-color="indigo darken-4"
                            >
                              {{ item.HlvName }}
                            </v-chip>
                            <span
                              v-else-if="index === 2"
                              class="grey--text text-caption"
                            >
                              +{{ filterHierarchicalLevels.length - 2 }}
                            </span>
                          </template>
                        </v-autocomplete>
                      </v-col>

                      <!-- Tipo de Cargo (Cat. Ocupacional) (Multiselect) -->
                      <v-col cols="12" md="4">
                        <v-autocomplete
                          v-model="filterTypePositions"
                          :items="typePositionOptions"
                          item-text="DedDescription"
                          item-value="DedValue"
                          label="Tipo de Cargo / Cat. Ocup. (Multiselect)"
                          multiple
                          chips
                          small-chips
                          deletable-chips
                          clearable
                          outlined
                          dense
                          hide-details
                          placeholder="Todos los tipos"
                        >
                          <template v-slot:selection="{ item, index }">
                            <v-chip
                              v-if="index < 2"
                              x-small
                              color="teal lighten-4"
                              text-color="teal darken-4"
                            >
                              {{ item.DedDescription }}
                            </v-chip>
                            <span
                              v-else-if="index === 2"
                              class="grey--text text-caption"
                            >
                              +{{ filterTypePositions.length - 2 }}
                            </span>
                          </template>
                        </v-autocomplete>
                      </v-col>
                    </v-row>

                    <!-- Fila 2: Filtros de Atributos del Puesto -->
                    <v-row dense class="mt-2">
                      <!-- Personal Fiscalizado -->
                      <v-col cols="12" sm="6" md="3">
                        <v-select
                          v-model="filterIsAuditable"
                          :items="auditableFilterOptions"
                          label="Personal Fiscalizado"
                          outlined
                          dense
                          clearable
                          hide-details
                        />
                      </v-col>

                      <!-- Personal de Confianza -->
                      <v-col cols="12" sm="6" md="3">
                        <v-select
                          v-model="filterIsConfidence"
                          :items="confidenceFilterOptions"
                          label="Personal de Confianza"
                          outlined
                          dense
                          clearable
                          hide-details
                        />
                      </v-col>

                      <!-- Cargo Fijo / Variable -->
                      <v-col cols="12" sm="6" md="2">
                        <v-select
                          v-model="filterIsPermanent"
                          :items="permanentFilterOptions"
                          label="Fijo / Variable"
                          outlined
                          dense
                          clearable
                          hide-details
                        />
                      </v-col>

                      <!-- Filtro por Área (Multiselect) -->
                      <v-col cols="12" sm="6" md="4">
                        <v-autocomplete
                          v-model="filterAreas"
                          :items="areaOptions"
                          item-text="GenDescription"
                          item-value="GenID"
                          label="Área (Multiselect)"
                          multiple
                          chips
                          small-chips
                          deletable-chips
                          clearable
                          outlined
                          dense
                          hide-details
                          placeholder="Todas las áreas"
                        >
                          <template v-slot:selection="{ item, index }">
                            <v-chip
                              v-if="index < 1"
                              x-small
                              color="amber lighten-4"
                              text-color="amber darken-4"
                            >
                              {{ item.GenDescription }}
                            </v-chip>
                            <span
                              v-else-if="index === 1"
                              class="grey--text text-caption"
                            >
                              +{{ filterAreas.length - 1 }}
                            </span>
                          </template>
                        </v-autocomplete>
                      </v-col>
                    </v-row>

                    <!-- Fila 3: Búsqueda rápida por nombre -->
                    <v-row dense class="mt-2">
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="filterSearchText"
                          label="Buscar dentro de puestos filtrados..."
                          prepend-inner-icon="mdi-magnify"
                          outlined
                          dense
                          clearable
                          hide-details
                        />
                      </v-col>

                      <!-- Botones de Acción sobre Puestos Filtrados -->
                      <v-col
                        cols="12"
                        md="6"
                        class="d-flex align-center justify-end flex-wrap gap-1"
                      >
                        <v-btn
                          small
                          color="primary"
                          class="text-none mr-1 mb-1"
                          @click="selectFilteredPositions"
                          :disabled="filteredPositions.length === 0"
                        >
                          <v-icon left small
                            >mdi-checkbox-multiple-marked</v-icon
                          >
                          Seleccionar Filtrados ({{ filteredPositions.length }})
                        </v-btn>

                        <v-btn
                          small
                          color="warning"
                          outlined
                          class="text-none mr-1 mb-1"
                          @click="unselectFilteredPositions"
                          :disabled="
                            filteredPositions.length === 0 ||
                            selectedPstIDs.length === 0
                          "
                        >
                          <v-icon left small
                            >mdi-checkbox-multiple-blank-outline</v-icon
                          >
                          Quitar Filtrados
                        </v-btn>

                        <v-btn
                          small
                          color="deep-purple"
                          outlined
                          class="text-none mr-1 mb-1"
                          @click="replaceWithFilteredPositions"
                          :disabled="filteredPositions.length === 0"
                        >
                          <v-icon left small>mdi-swap-horizontal</v-icon>
                          Reemplazar con Filtrados
                        </v-btn>

                        <v-btn
                          small
                          color="grey darken-1"
                          text
                          class="text-none mb-1"
                          @click="clearFilters"
                        >
                          <v-icon left small>mdi-filter-remove-outline</v-icon>
                          Limpiar Filtros
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-col>
          </v-row>

          <!-- SELECTOR PRINCIPAL DE PUESTOS ASIGNADOS -->
          <v-row v-if="!props.item.IsGeneral">
            <v-col cols="12">
              <div class="d-flex align-center justify-space-between mb-1">
                <v-label><b>Puestos Asignados al Documento *</b></v-label>
                <v-btn
                  v-if="selectedPstIDs.length > 0"
                  x-small
                  color="red darken-1"
                  text
                  class="text-none"
                  @click="clearSelectedPositions"
                >
                  <v-icon x-small left>mdi-trash-can-outline</v-icon>
                  Quitar todos ({{ selectedPstIDs.length }})
                </v-btn>
              </div>

              <v-autocomplete
                v-model="selectedPstIDs"
                :items="allPositionItems"
                item-text="PstName"
                item-value="PstID"
                multiple
                clearable
                outlined
                dense
                :rules="[rules.requiredPositions]"
                :loading="positionsLoading"
                :disabled="props.item.IsGeneral == true"
                :search-input.sync="positionSearch"
                placeholder="Seleccione uno o varios puestos..."
                @focus="ensurePositionsLoaded()"
                @blur="clearPositionSearch"
                @change="onPositionsChange"
              >
                <!-- Renderizado de items en dropdown con información enriquecida -->
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium">
                      {{ item.PstName }}
                    </v-list-item-title>
                    <v-list-item-subtitle
                      class="caption grey--text text--darken-1 mt-1"
                    >
                      <span v-if="item.AreName">🏛️ {{ item.AreName }} | </span>
                      <span v-if="item.TypePayrollName"
                        >📋 {{ item.TypePayrollName }} |
                      </span>
                      <span v-if="item.HlvName">🎖️ {{ item.HlvName }} | </span>
                      <span
                        :class="
                          item.PstIsAuditable
                            ? 'green--text font-weight-bold'
                            : 'grey--text'
                        "
                      >
                        {{
                          item.PstIsAuditable ? "Fiscalizado" : "No Fiscalizado"
                        }}
                      </span>
                      <span
                        v-if="item.PstIsConfidence"
                        class="orange--text font-weight-bold ml-1"
                      >
                        | Confianza
                      </span>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </template>

                <!-- Renderizado de selección (chips) -->
                <template v-slot:selection="{ item, index }">
                  <v-chip
                    v-if="index < 3"
                    small
                    close
                    color="primary lighten-5"
                    text-color="primary"
                    class="mr-1 my-1"
                    @click:close="removeSelectedPosition(item)"
                  >
                    {{ getPositionText(item) }}
                  </v-chip>
                  <span
                    v-else-if="index === 3"
                    class="grey--text text--darken-1 font-weight-bold ml-1"
                  >
                    +{{ selectedPstIDs.length - 3 }} puestos más
                  </span>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>
        </v-container>
      </template>
    </s-crud>

    <!-- DIÁLOGO / MODAL PARA GESTIONAR PUESTOS CON TABLA INTERACTIVA -->
    <v-dialog v-model="dialogManagePositions" max-width="1100px" scrollable>
      <v-card>
        <v-card-title
          class="primary white--text py-3 px-4 d-flex align-center justify-space-between"
        >
          <div class="d-flex align-center">
            <v-icon color="white" class="mr-2">mdi-table-cog</v-icon>
            <span class="text-subtitle-1 font-weight-bold"
              >Gestión y Selección de Puestos</span
            >
          </div>
          <v-btn icon small dark @click="dialogManagePositions = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-4">
          <!-- Resumen y búsqueda rápida dentro del modal -->
          <v-row align="center" justify="space-between" class="mb-2">
            <v-col cols="12" md="6">
              <v-text-field
                v-model="modalSearchText"
                label="Buscar por nombre de puesto, área o planilla..."
                prepend-inner-icon="mdi-magnify"
                outlined
                dense
                clearable
                hide-details
              />
            </v-col>
            <v-col
              cols="12"
              md="6"
              class="d-flex align-center justify-end flex-wrap gap-1"
            >
              <v-chip small color="primary" class="mr-2">
                Seleccionados: {{ selectedPstIDs.length }} /
                {{ allPositions.length }}
              </v-chip>
              <v-btn
                small
                color="primary"
                class="text-none mr-1"
                @click="selectAllModalFiltered"
              >
                <v-icon left small>mdi-check-all</v-icon>
                Seleccionar Visibles
              </v-btn>
              <v-btn
                small
                color="warning"
                outlined
                class="text-none"
                @click="unselectAllModalFiltered"
              >
                <v-icon left small>mdi-close-box-multiple-outline</v-icon>
                Deseleccionar Visibles
              </v-btn>
            </v-col>
          </v-row>

          <!-- Tabla de Puestos -->
          <v-data-table
            :headers="positionTableHeaders"
            :items="modalTablePositions"
            :loading="positionsLoading"
            item-key="PstID"
            dense
            class="elevation-1"
            :items-per-page="15"
            :footer-props="{ 'items-per-page-options': [15, 30, 50, 100] }"
          >
            <!-- Checkbox de selección -->
            <template v-slot:item.selected="{ item }">
              <v-simple-checkbox
                :value="isPositionSelected(item.PstID)"
                @input="togglePositionSelection(item.PstID)"
                color="primary"
              />
            </template>

            <!-- Nombre de Puesto -->
            <template v-slot:item.PstName="{ item }">
              <span class="font-weight-medium">{{ item.PstName }}</span>
            </template>

            <!-- Fiscalizado -->
            <template v-slot:item.PstIsAuditable="{ item }">
              <v-chip
                x-small
                :color="item.PstIsAuditable ? 'success' : 'grey lighten-2'"
                :text-color="item.PstIsAuditable ? 'white' : 'grey darken-2'"
              >
                {{ item.PstIsAuditable ? "Sí" : "No" }}
              </v-chip>
            </template>

            <!-- Confianza -->
            <template v-slot:item.PstIsConfidence="{ item }">
              <v-chip
                x-small
                :color="item.PstIsConfidence ? 'warning' : 'grey lighten-2'"
                :text-color="item.PstIsConfidence ? 'white' : 'grey darken-2'"
              >
                {{ item.PstIsConfidence ? "Sí" : "No" }}
              </v-chip>
            </template>

            <!-- Fijo / Variable -->
            <template v-slot:item.PstIsPermanent="{ item }">
              <span class="caption">
                {{
                  item.PstIsPermanent === 1 || item.PstIsPermanent === true
                    ? "Fijo"
                    : "Variable"
                }}
              </span>
            </template>
          </v-data-table>
        </v-card-text>

        <v-card-actions class="grey lighten-3 px-4 py-2 border-top">
          <span class="caption text--secondary">
            Total asignados al documento:
            <strong>{{ selectedPstIDs.length }}</strong> puestos
          </span>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="dialogManagePositions = false"
            >Aceptar</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import _sPositionDocument from "@/services/HumanResource/HmnPositionDocumentService";
import _GenUploadFiles from "@/services/General/GenUploadFiles";
import _sPositionService from "@/services/General/PositionService";
import _sDefinition from "@/services/General/DefinitionService";
import _sHierarchicalLevelService from "@/services/HumanResource/OrganizationalStructure/HmnHierarchicalLevelService";
import _sGenericService from "@/services/General/GenericService";
import Service from "@/services/Service";

export default {
  name: "HmnPositionDocument",

  data() {
    return {
      selectedFiles: [],
      selectedPstIDs: [],
      allPositions: [],
      positionsLoading: false,
      positionSearch: "",
      filterPstName: "",
      dialogManagePositions: false,
      modalSearchText: "",
      filterPanelOpen: 0, // Abierto por defecto

      // Filtros avanzados para selección de puestos
      filterPayrolls: [],
      filterHierarchicalLevels: [],
      filterTypePositions: [],
      filterAreas: [],
      filterIsAuditable: null,
      filterIsConfidence: null,
      filterIsPermanent: null,
      filterSearchText: "",

      // Opciones de listas
      payrollOptions: [],
      hierarchicalLevelOptions: [],
      typePositionOptions: [],
      areaOptions: [],

      auditableFilterOptions: [
        { text: "Todos", value: null },
        { text: "Solo Fiscalizados (Sí)", value: true },
        { text: "No Fiscalizados (No)", value: false },
      ],

      confidenceFilterOptions: [
        { text: "Todos", value: null },
        { text: "Solo de Confianza (Sí)", value: true },
        { text: "No es de Confianza (No)", value: false },
      ],

      permanentFilterOptions: [
        { text: "Todos", value: null },
        { text: "Solo Fijos", value: 1 },
        { text: "Solo Variables", value: 0 },
      ],

      // Cabeceras de tabla del modal
      positionTableHeaders: [
        { text: "Sel.", value: "selected", width: "50px", sortable: false },
        { text: "Cargo / Puesto", value: "PstName" },
        { text: "Área", value: "AreName" },
        { text: "Planilla", value: "TypePayrollName" },
        { text: "Nivel Jerárquico", value: "HlvName" },
        { text: "Cat. Ocupacional", value: "TypePositionName" },
        { text: "Fiscalizado", value: "PstIsAuditable", width: "90px" },
        { text: "Confianza", value: "PstIsConfidence", width: "80px" },
        { text: "Fijo/Var.", value: "PstIsPermanent", width: "70px" },
      ],

      // Lista de componentes / formatos disponibles para selección rápida
      availableComponents: [
        "FormatSSTCritical",
        "FormatRisst",
        "FormatRit",
        "FormatCodeOfConduct",
        "FormatAddressDeclaration",
        "FormatDiningDeduction",
        "FormatBackgroundDeclaration",
        "FormatSubstanceDeclaration",
        "FormatOvertimeCompensation",
        "FormatDigitalConsentAri",
        "FormatDigitalConsentAriDeliverable",
      ],

      // Reglas de validación
      rules: {
        required: (v) =>
          (!!v && v.toString().trim().length > 0) ||
          "Este campo es obligatorio",
        requiredComponentOrFile: (v) => true, // se valida en save()
        requiredPositions: (v) => true, // se valida en save() junto con IsGeneral
      },

      filter: {
        PstID: null,
      },

      config: {
        model: {
          PdcID: "ID",
          PdcDocumentTitle: "string",
          PdcComponentName: "string",
          PdcFileName: "string",
          IsGeneral: "bool",
          SecStatus: "status",
          PstNames: "string",
        },
        service: _sPositionDocument,
        headers: [
          { text: "ID", value: "PdcID", width: "5%" },
          { text: "Título", value: "PdcDocumentTitle", width: "20%" },
          { text: "Componente Vue", value: "PdcComponentName", width: "14%" },
          { text: "PDF Entregable", value: "PdcFileName", width: "16%" },
          { text: "Tipo", value: "IsGeneral", width: "10%" },
          { text: "Puestos Asignados", value: "PstNames", width: "35%" },
        ],
      },
    };
  },

  computed: {
    allPositionItems() {
      return this.allPositions.map((p) => ({
        ...p,
        GenID: p.PstID,
        GenDescription: p.PstName,
      }));
    },

    activeFilterCount() {
      let count = 0;
      if (this.filterPayrolls && this.filterPayrolls.length > 0) count++;
      if (
        this.filterHierarchicalLevels &&
        this.filterHierarchicalLevels.length > 0
      )
        count++;
      if (this.filterTypePositions && this.filterTypePositions.length > 0)
        count++;
      if (this.filterAreas && this.filterAreas.length > 0) count++;
      if (
        this.filterIsAuditable !== null &&
        this.filterIsAuditable !== undefined
      )
        count++;
      if (
        this.filterIsConfidence !== null &&
        this.filterIsConfidence !== undefined
      )
        count++;
      if (
        this.filterIsPermanent !== null &&
        this.filterIsPermanent !== undefined
      )
        count++;
      if (this.filterSearchText && this.filterSearchText.trim().length > 0)
        count++;
      return count;
    },

    filteredPositions() {
      return this.allPositions.filter((pos) => {
        // Filtro Planilla (multiselect) usando LbrID o TypePayroll contra DedHelper
        if (this.filterPayrolls && this.filterPayrolls.length > 0) {
          const posLbr = pos.LbrID ? String(pos.LbrID).trim() : "";
          const posTypePayroll =
            pos.TypePayroll !== null && pos.TypePayroll !== undefined
              ? String(pos.TypePayroll).trim()
              : "";
          const matchPayroll = this.filterPayrolls.some((selectedVal) => {
            if (!selectedVal) return false;
            const sel = String(selectedVal).trim();
            if (posLbr) {
              if (posLbr === sel) return true;
              if (parseInt(posLbr, 10) === parseInt(sel, 10)) return true;
            }
            if (posTypePayroll) {
              if (posTypePayroll === sel) return true;
              if (parseInt(posTypePayroll, 10) === parseInt(sel, 10))
                return true;
            }
            return false;
          });
          if (!matchPayroll) return false;
        }

        // Filtro Nivel Jerárquico (multiselect)
        if (
          this.filterHierarchicalLevels &&
          this.filterHierarchicalLevels.length > 0
        ) {
          if (!this.filterHierarchicalLevels.includes(pos.HlvID)) return false;
        }

        // Filtro Tipo de Cargo (multiselect)
        if (this.filterTypePositions && this.filterTypePositions.length > 0) {
          if (!this.filterTypePositions.includes(pos.TypePosition))
            return false;
        }

        // Filtro Área (multiselect)
        if (this.filterAreas && this.filterAreas.length > 0) {
          if (!this.filterAreas.includes(pos.AreID)) return false;
        }

        // Filtro Fiscalizado
        if (
          this.filterIsAuditable !== null &&
          this.filterIsAuditable !== undefined
        ) {
          const isAud = pos.PstIsAuditable === true || pos.PstIsAuditable === 1;
          if (isAud !== this.filterIsAuditable) return false;
        }

        // Filtro Confianza
        if (
          this.filterIsConfidence !== null &&
          this.filterIsConfidence !== undefined
        ) {
          const isConf =
            pos.PstIsConfidence === true || pos.PstIsConfidence === 1;
          if (isConf !== this.filterIsConfidence) return false;
        }

        // Filtro Fijo / Variable
        if (
          this.filterIsPermanent !== null &&
          this.filterIsPermanent !== undefined
        ) {
          const isPerm =
            pos.PstIsPermanent === 1 || pos.PstIsPermanent === true ? 1 : 0;
          if (isPerm !== this.filterIsPermanent) return false;
        }

        // Filtro Texto
        if (this.filterSearchText && this.filterSearchText.trim().length > 0) {
          const q = this.filterSearchText.trim().toLowerCase();
          const name = (pos.PstName || "").toLowerCase();
          if (!name.includes(q)) return false;
        }

        return true;
      });
    },

    modalTablePositions() {
      let list = this.allPositions;

      // Si hay filtros activos en el panel, aplicarlos
      if (this.activeFilterCount > 0) {
        list = this.filteredPositions;
      }

      // Si hay búsqueda dentro del modal
      if (this.modalSearchText && this.modalSearchText.trim().length > 0) {
        const q = this.modalSearchText.trim().toLowerCase();
        list = list.filter((p) => {
          const name = (p.PstName || "").toLowerCase();
          const area = (p.AreName || "").toLowerCase();
          const payroll = (
            p.TypePayrollName ||
            p.LaborRegimeText ||
            ""
          ).toLowerCase();
          const hlv = (p.HlvName || "").toLowerCase();
          return (
            name.includes(q) ||
            area.includes(q) ||
            payroll.includes(q) ||
            hlv.includes(q)
          );
        });
      }

      return list;
    },
  },

  async mounted() {
    await this.loadAllFilterOptions();
    await this.loadAllPositions();
  },

  methods: {
    async loadAllFilterOptions() {
      const userId = this.$fun ? this.$fun.getUserID() : 1;

      // 1. Cargar Planillas (DefID 1060)
      try {
        const respPayroll = await _sDefinition.definitiongroup(1060, 0, userId);
        const data = Array.isArray(respPayroll.data) ? respPayroll.data : [];
        this.payrollOptions = data.map((item) => {
          const code = item.DedHelper
            ? String(item.DedHelper).trim()
            : String(item.DedValue).trim();
          return {
            ...item,
            DedHelperValue: code,
          };
        });
      } catch (e) {
        console.warn("Error al cargar opciones de Planilla:", e);
      }

      // 2. Cargar Niveles Jerárquicos
      try {
        const respHlv = await _sHierarchicalLevelService.list(userId);
        const arr = respHlv?.data || respHlv || [];
        this.hierarchicalLevelOptions = Array.isArray(arr) ? arr : [];
      } catch (e) {
        console.warn("Error al cargar niveles jerárquicos:", e);
      }

      // 3. Cargar Tipo de Cargo (DefID 1039)
      try {
        const respTypePos = await _sDefinition.definitiongroup(1039, 0, userId);
        this.typePositionOptions = Array.isArray(respTypePos.data)
          ? respTypePos.data
          : [];
      } catch (e) {
        console.warn("Error al cargar tipos de cargo:", e);
      }

      // 4. Cargar Áreas
      try {
        const respAreas = await Service.post(
          _sGenericService.getUrlSearchArea(),
          {},
          {
            params: {
              TypeArea: 3,
              search: "",
              requestID: userId,
            },
          },
        );
        this.areaOptions = Array.isArray(respAreas.data) ? respAreas.data : [];
      } catch (e) {
        console.warn("Error al cargar áreas:", e);
      }
    },

    async loadAllPositions() {
      this.positionsLoading = true;
      try {
        const dtr = {
          length: 10000,
          start: 0,
          orderColumn: 2,
          orderDir: "ASC",
          searchText: "",
          filter: {
            PstStatus: 1,
            IsAdmin: 1,
            UsrID: this.$fun ? this.$fun.getUserID() : 1,
          },
        };
        const resp = await _sPositionService.pagination(dtr);
        const list = resp?.data?.data || resp?.data || [];
        this.allPositions = Array.isArray(list) ? list : [];
      } catch (err) {
        console.warn("Error al cargar listado completo de puestos:", err);
      } finally {
        this.positionsLoading = false;
      }
    },

    async ensurePositionsLoaded() {
      if (!this.allPositions || this.allPositions.length === 0) {
        await this.loadAllPositions();
      }
    },

    onFilesChanged(files) {
      this.selectedFiles = Array.isArray(files) ? files : files ? [files] : [];
    },

    downloadCurrentFile(fileName) {
      if (!fileName) return;
      _GenUploadFiles.getFile("PATH_DOCUMENTS_POSITION_RRHH", fileName);
    },

    clearForm() {
      this.selectedFiles = [];
      this.selectedPstIDs = [];
      this.positionSearch = "";
      this.clearFilters();
    },

    clearFilters() {
      this.filterPayrolls = [];
      this.filterHierarchicalLevels = [];
      this.filterTypePositions = [];
      this.filterAreas = [];
      this.filterIsAuditable = null;
      this.filterIsConfidence = null;
      this.filterIsPermanent = null;
      this.filterSearchText = "";
      this.modalSearchText = "";
    },

    parsePositionList(pstNames) {
      if (!pstNames || typeof pstNames !== "string") return [];
      return pstNames
        .split(",")
        .map((x) => x.trim())
        .filter((x) => x.length > 0);
    },

    clearPositionSearch() {
      this.positionSearch = "";
    },

    getPositionId(item) {
      if (item && typeof item === "object") return item.PstID || item.GenID;
      return item;
    },

    getPositionText(item) {
      if (item && typeof item === "object")
        return item.PstName || item.GenDescription;
      const found = this.allPositions.find((p) => p.PstID === item);
      return found ? found.PstName : item;
    },

    removeSelectedPosition(item) {
      const id = this.getPositionId(item);
      this.selectedPstIDs = this.selectedPstIDs.filter((x) => x !== id);
      this.positionSearch = "";
    },

    clearSelectedPositions() {
      this.selectedPstIDs = [];
      this.positionSearch = "";
    },

    onPositionsChange(values) {
      this.selectedPstIDs = Array.isArray(values)
        ? values
        : values
        ? [values]
        : [];
      this.positionSearch = "";
    },

    // Acciones de Selección Masiva
    selectFilteredPositions() {
      const idsToAdd = this.filteredPositions.map((p) => p.PstID);
      const set = new Set([...this.selectedPstIDs, ...idsToAdd]);
      const addedCount = set.size - this.selectedPstIDs.length;
      this.selectedPstIDs = Array.from(set);

      this.$fun.alert(
        `Se agregaron ${addedCount} puesto(s) a la selección. Total: ${this.selectedPstIDs.length}`,
        "success",
      );
    },

    unselectFilteredPositions() {
      const idsToRemove = new Set(this.filteredPositions.map((p) => p.PstID));
      const beforeCount = this.selectedPstIDs.length;
      this.selectedPstIDs = this.selectedPstIDs.filter(
        (id) => !idsToRemove.has(id),
      );
      const removedCount = beforeCount - this.selectedPstIDs.length;

      this.$fun.alert(
        `Se removieron ${removedCount} puesto(s) de la selección. Restan: ${this.selectedPstIDs.length}`,
        "info",
      );
    },

    replaceWithFilteredPositions() {
      this.selectedPstIDs = this.filteredPositions.map((p) => p.PstID);
      this.$fun.alert(
        `Se asignaron exactamente los ${this.selectedPstIDs.length} puesto(s) filtrados.`,
        "success",
      );
    },

    // Funciones del Modal de Tabla
    isPositionSelected(pstId) {
      return this.selectedPstIDs.includes(pstId);
    },

    togglePositionSelection(pstId) {
      if (this.isPositionSelected(pstId)) {
        this.selectedPstIDs = this.selectedPstIDs.filter((id) => id !== pstId);
      } else {
        this.selectedPstIDs.push(pstId);
      }
    },

    selectAllModalFiltered() {
      const ids = this.modalTablePositions.map((p) => p.PstID);
      const set = new Set([...this.selectedPstIDs, ...ids]);
      this.selectedPstIDs = Array.from(set);
    },

    unselectAllModalFiltered() {
      const idsToRemove = new Set(this.modalTablePositions.map((p) => p.PstID));
      this.selectedPstIDs = this.selectedPstIDs.filter(
        (id) => !idsToRemove.has(id),
      );
    },

    rowSelected(items) {
      this.selectedFiles = [];
      this.clearFilters();

      if (items.length > 0) {
        const doc = items[0];
        if (doc.PstIDs) {
          this.selectedPstIDs = doc.PstIDs.split(",")
            .map((id) => parseInt(id.trim()))
            .filter((id) => !isNaN(id));
          this.ensurePositionsLoaded();
        } else {
          this.selectedPstIDs = [];
        }

        // Restaurar filtros del documento
        if (doc.TypePayrollCSV) {
          this.filterPayrolls = doc.TypePayrollCSV.split(",")
            .map((x) => x.trim())
            .filter((x) => x.length > 0);
        }
        if (doc.HlvIDsCSV) {
          this.filterHierarchicalLevels = doc.HlvIDsCSV.split(",")
            .map((x) => parseInt(x.trim()))
            .filter((x) => !isNaN(x));
        }
        if (doc.TypePositionCSV) {
          this.filterTypePositions = doc.TypePositionCSV.split(",")
            .map((x) => parseInt(x.trim()))
            .filter((x) => !isNaN(x));
        }
        if (doc.AreIDsCSV) {
          this.filterAreas = doc.AreIDsCSV.split(",")
            .map((x) => parseInt(x.trim()))
            .filter((x) => !isNaN(x));
        }
        if (doc.IsAuditable !== null && doc.IsAuditable !== undefined) {
          this.filterIsAuditable =
            doc.IsAuditable === true || doc.IsAuditable === 1;
        }
        if (doc.IsConfidence !== null && doc.IsConfidence !== undefined) {
          this.filterIsConfidence =
            doc.IsConfidence === true || doc.IsConfidence === 1;
        }
        if (doc.IsPermanent !== null && doc.IsPermanent !== undefined) {
          this.filterIsPermanent =
            doc.IsPermanent === true || doc.IsPermanent === 1 ? 1 : 0;
        }
      } else {
        this.selectedPstIDs = [];
      }
      this.positionSearch = "";
    },

    validateForm(item) {
      // Título obligatorio
      if (!item.PdcDocumentTitle || item.PdcDocumentTitle.trim().length === 0) {
        this.$fun.alert("El Título del Documento es obligatorio.", "warning");
        return false;
      }

      // Debe tener al menos componente Vue o archivo PDF
      const hasComponent =
        item.PdcComponentName && item.PdcComponentName.trim().length > 0;
      const hasFiles = this.selectedFiles && this.selectedFiles.length > 0;
      const hasExistingFile =
        item.PdcFileName && item.PdcFileName.trim().length > 0;

      if (!hasComponent && !hasFiles && !hasExistingFile) {
        this.$fun.alert(
          "Debe seleccionar un Componente Vue o subir al menos un archivo PDF.",
          "warning",
        );
        return false;
      }

      // Si no es general, debe tener al menos un puesto o al menos una regla de filtro
      const hasFilters = this.activeFilterCount > 0;
      if (!item.IsGeneral && this.selectedPstIDs.length === 0 && !hasFilters) {
        this.$fun.alert(
          "Debe seleccionar al menos un puesto, configurar al menos una regla de filtro, o marcar como Documento General.",
          "warning",
        );
        return false;
      }

      return true;
    },

    buildDocPayload(item, fileName = null) {
      const isNew = !item.PdcID || item.PdcID === 0;
      let secStatus = 1;
      if (
        !isNew &&
        item.SecStatus !== undefined &&
        item.SecStatus !== null &&
        item.SecStatus !== ""
      ) {
        secStatus = Number(item.SecStatus);
      }

      return {
        PdcID: item.PdcID || 0,
        PdcFileName: fileName || item.PdcFileName || null,
        PdcComponentName: item.PdcComponentName || null,
        PdcDocumentTitle: item.PdcDocumentTitle || null,
        IsGeneral: item.IsGeneral || false,
        TypePayrollCSV:
          this.filterPayrolls && this.filterPayrolls.length > 0
            ? this.filterPayrolls.join(",")
            : null,
        HlvIDsCSV:
          this.filterHierarchicalLevels &&
          this.filterHierarchicalLevels.length > 0
            ? this.filterHierarchicalLevels.join(",")
            : null,
        TypePositionCSV:
          this.filterTypePositions && this.filterTypePositions.length > 0
            ? this.filterTypePositions.join(",")
            : null,
        AreIDsCSV:
          this.filterAreas && this.filterAreas.length > 0
            ? this.filterAreas.join(",")
            : null,
        IsAuditable:
          this.filterIsAuditable !== null &&
          this.filterIsAuditable !== undefined
            ? this.filterIsAuditable
            : null,
        IsConfidence:
          this.filterIsConfidence !== null &&
          this.filterIsConfidence !== undefined
            ? this.filterIsConfidence
            : null,
        IsPermanent:
          this.filterIsPermanent !== null &&
          this.filterIsPermanent !== undefined
            ? this.filterIsPermanent === 1
            : null,
        SecStatus: secStatus,
        SecCreate: item.SecCreate || new Date().toISOString(),
        UsrCreateID: item.UsrCreateID || this.$fun.getUserID(),
        SecUpdate: new Date().toISOString(),
        UsrUpdateID: this.$fun.getUserID(),
      };
    },

    async save(item) {
      if (!this.validateForm(item)) return;

      // Si es nuevo y hay archivos, procesar cada archivo
      if (!item.PdcID && this.selectedFiles && this.selectedFiles.length > 0) {
        await this.saveMultipleFiles(item);
      } else if (
        !item.PdcID &&
        (!this.selectedFiles || this.selectedFiles.length === 0)
      ) {
        // Nuevo sin archivos (solo formato Vue)
        await this.saveSingleFormat(item);
      } else {
        // Modo edición
        await this.saveEdit(item);
      }
    },

    async saveSingleFormat(item) {
      try {
        const docObj = this.buildDocPayload(item, null);
        const PstIDsCSV =
          this.selectedPstIDs.length > 0 ? this.selectedPstIDs.join(",") : null;
        await _sPositionDocument.save(docObj, this.$fun.getUserID(), PstIDsCSV);
        this.$fun.alert("Formato guardado correctamente.", "success");
        this.$refs.scrud.refresh();
        this.selectedPstIDs = [];
      } catch (err) {
        let msg = "Error al guardar formato";
        if (err && err.response && err.response.data)
          msg =
            err.response.data.message ||
            err.response.data.Message ||
            JSON.stringify(err.response.data);
        this.$fun.alert(msg, "error");
      }
    },

    async saveMultipleFiles(item) {
      const ok = [];
      const fail = [];

      for (const file of this.selectedFiles) {
        const fileNameBase = file.name.replace(/\.[^/.]+$/, "");

        try {
          await _GenUploadFiles.upload(
            file,
            fileNameBase,
            "PATH_DOCUMENTS_POSITION_RRHH",
            false,
          );
        } catch (err) {
          let msg = "Error al subir el archivo";
          if (err && err.response) {
            if (err.response.status === 409)
              msg = "El archivo ya existe en el servidor";
            else if (err.response.data)
              msg =
                err.response.data.message ||
                err.response.data.Message ||
                JSON.stringify(err.response.data);
          }
          fail.push({ file: file.name, error: msg });
          continue;
        }

        try {
          const docObj = this.buildDocPayload(item, file.name);
          const PstIDsCSV =
            this.selectedPstIDs.length > 0
              ? this.selectedPstIDs.join(",")
              : null;
          await _sPositionDocument.save(
            docObj,
            this.$fun.getUserID(),
            PstIDsCSV,
          );
          ok.push(file.name);
        } catch (err) {
          let msg = "Error al guardar registro";
          if (err && err.response && err.response.data)
            msg =
              err.response.data.message ||
              err.response.data.Message ||
              JSON.stringify(err.response.data);
          fail.push({ file: file.name, error: msg });
        }
      }

      if (ok.length > 0) {
        this.$fun.alert(
          `${ok.length} documento(s) guardado(s) correctamente.`,
          "success",
        );
        this.$refs.scrud.refresh();
      }
      if (fail.length > 0) {
        const msgs = fail.map((f) => `${f.file}: ${f.error}`).join("\n");
        this.$fun.alert(`Errores:\n${msgs}`, "error");
      }

      this.selectedFiles = [];
      this.selectedPstIDs = [];
    },

    async saveEdit(item) {
      let fileName = item.PdcFileName || null;

      // Si se seleccionó un nuevo archivo PDF para reemplazar / asignar
      if (this.selectedFiles && this.selectedFiles.length > 0) {
        const file = this.selectedFiles[0];
        const fileNameBase = file.name.replace(/\.[^/.]+$/, "");

        try {
          await _GenUploadFiles.upload(
            file,
            fileNameBase,
            "PATH_DOCUMENTS_POSITION_RRHH",
            true,
          );
          fileName = file.name;
        } catch (err) {
          let msg = "Error al subir el nuevo archivo PDF";
          if (err && err.response) {
            if (err.response.status === 409)
              msg = "El archivo ya existe en el servidor";
            else if (err.response.data)
              msg =
                err.response.data.message ||
                err.response.data.Message ||
                JSON.stringify(err.response.data);
          }
          this.$fun.alert(msg, "error");
          return;
        }
      }

      const docObj = this.buildDocPayload(item, fileName);
      const PstIDsCSV =
        this.selectedPstIDs.length > 0 ? this.selectedPstIDs.join(",") : null;

      try {
        const resp = await _sPositionDocument.save(
          docObj,
          this.$fun.getUserID(),
          PstIDsCSV,
        );
        if (resp.status === 200) {
          this.$fun.alert("Guardado correctamente", "success");
          this.$refs.scrud.refresh();
          this.selectedFiles = [];
          this.selectedPstIDs = [];
        }
      } catch (err) {
        let msg = "Error al guardar cambios";
        if (err && err.response && err.response.data)
          msg =
            err.response.data.message ||
            err.response.data.Message ||
            JSON.stringify(err.response.data);
        this.$fun.alert(msg, "error");
      }
    },
  },
};
</script>

<style scoped>
.bg-filter-header {
  background-color: #f1f5f9 !important;
}

.gap-1 {
  gap: 6px;
}
</style>
